import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Copy, Check, Eye, EyeOff, ExternalLink, Key, 
  User, Lock, Server, Shield, AlertTriangle 
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

/**
 * MatchTraderCredentials Component
 * 
 * Displays Match-Trader trading account credentials with:
 * - Secure password visibility toggle
 * - One-click copy functionality
 * - Direct link to Match-Trader platform
 */
const MatchTraderCredentials = ({ 
  credentials, 
  accountStatus = 'active',
  showTitle = true,
  compact = false 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Default credentials structure if not provided
  const creds = credentials || {
    login: null,
    password: null,
    server: 'demo.match-trader.com',
    platform_url: 'https://trade.match-trader.com'
  };

  const handleCopy = async (field, value) => {
    if (!value) return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success(`${field} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const CopyButton = ({ field, value }) => (
    <button
      onClick={() => handleCopy(field, value)}
      className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
      disabled={!value}
      data-testid={`copy-${field.toLowerCase()}`}
    >
      {copiedField === field ? (
        <Check className="w-4 h-4 text-green-400" />
      ) : (
        <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
      )}
    </button>
  );

  // No credentials yet
  if (!creds.login) {
    return (
      <div className={`rounded-xl border border-yellow-500/20 bg-yellow-500/5 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-center gap-2 text-yellow-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">Trading credentials pending</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Your Match-Trader account is being created. Credentials will appear here shortly.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-white">Trading Credentials</span>
          </div>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
            Active
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1.5">
            <span className="text-gray-400">Login:</span>
            <div className="flex items-center gap-1">
              <span className="font-mono text-white">{creds.login}</span>
              <CopyButton field="Login" value={creds.login} />
            </div>
          </div>
          <div className="flex items-center justify-between bg-black/20 rounded px-2 py-1.5">
            <span className="text-gray-400">Password:</span>
            <div className="flex items-center gap-1">
              <span className="font-mono text-white">
                {showPassword ? creds.password : '••••••••'}
              </span>
              <button 
                onClick={() => setShowPassword(!showPassword)}
                className="p-1 hover:bg-white/10 rounded"
              >
                {showPassword ? <EyeOff className="w-3 h-3 text-gray-400" /> : <Eye className="w-3 h-3 text-gray-400" />}
              </button>
              <CopyButton field="Password" value={creds.password} />
            </div>
          </div>
        </div>
        <a
          href={creds.platform_url || 'https://trade.match-trader.com'}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex items-center justify-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          Open Match-Trader Platform
        </a>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 overflow-hidden"
    >
      {showTitle && (
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Key className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-white text-sm">Match-Trader Credentials</h3>
              <p className="text-xs text-gray-500">Your trading account login details</p>
            </div>
          </div>
          <Badge className={`${
            accountStatus === 'active' || accountStatus === 'funded' 
              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
              : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          }`}>
            {accountStatus === 'funded' ? 'FUNDED' : accountStatus?.toUpperCase()}
          </Badge>
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Login */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-black/20">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Login ID</p>
              <p className="font-mono text-white font-medium">{creds.login}</p>
            </div>
          </div>
          <CopyButton field="Login" value={creds.login} />
        </div>

        {/* Password */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-black/20">
          <div className="flex items-center gap-3">
            <Lock className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Password</p>
              <p className="font-mono text-white font-medium">
                {showPassword ? creds.password : '••••••••••••'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
              data-testid="toggle-password"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4 text-gray-400 hover:text-white" />
              ) : (
                <Eye className="w-4 h-4 text-gray-400 hover:text-white" />
              )}
            </button>
            <CopyButton field="Password" value={creds.password} />
          </div>
        </div>

        {/* Server */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-black/20">
          <div className="flex items-center gap-3">
            <Server className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Server</p>
              <p className="font-mono text-white font-medium">{creds.server || 'demo.match-trader.com'}</p>
            </div>
          </div>
          <CopyButton field="Server" value={creds.server || 'demo.match-trader.com'} />
        </div>

        {/* Platform Link */}
        <a
          href={creds.platform_url || 'https://trade.match-trader.com'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full p-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium transition-all"
          data-testid="open-match-trader"
        >
          <ExternalLink className="w-4 h-4" />
          Open Match-Trader Platform
        </a>

        {/* Security Note */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
          <Shield className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-400">
            Keep your credentials secure. Never share your password with anyone.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default MatchTraderCredentials;
